<template>
  <div class="l-block l-mb-6">
    <div class="inbox-area">
      <div class="l-block__header">
        <TitleSub level="h2">
          <Icon2 size="18" variant="search" />
          <span class="title">調査したいドメイン</span>
        </TitleSub>
      </div>
      <div class="c-alertPos">
        <Input
          v-model="params.url"
          placeholder="（例）http://example.com"
          :disabled="isProcessing"
          :class="{
            alert: isEdited.url && error.url,
            warning: isEdited.url && isAlreadyChecked.url && !error.url
          }"
          v-focus="$route.name === 'competitiveSiteKeyword'"
          @focusout="focusoutUrl"
        />
        <template v-if="isEdited.url & !errorResponse">
          <Alert v-if="error.url">
            {{ error.url }}
          </Alert>
          <Alert v-else-if="isAlreadyChecked.url" variant="warning">
            {{ isAlreadyChecked.message }}
          </Alert>
        </template>
        <Alert v-if="errorResponse">
          {{ errorResponse }}
        </Alert>
      </div>
      <Button
        size="w230"
        fsize="f16"
        variant="main"
        :disabled="isError || isProcessing"
        :class="{ 'is-loading': isProcessing }"
        @click="submit"
      >
        調査する
      </Button>
    </div>
  </div>
</template>

<script>
import { USAGE_LABEL } from '@/constants';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Icon2 from '@/components/Icon2';
import Input from '@/components/Input';
import TitleSub from '@/components/TitleSub';
import apiProjects from '@/api/projects';
import Validation from '@/validation';
import utils from '@/utils';

export default {
  props: {
    project: {
      type: Object,
      required: true
    },
    url: {
      type: String,
      required: false,
      default: ''
    },
    items: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    }
  },
  components: {
    Alert,
    Button,
    Icon2,
    Input,
    TitleSub
  },
  data() {
    return {
      error: {
        url: ''
      },
      errorResponse: '',
      params: {
        url: ''
      },
      isEdited: {
        url: false
      },
      isAlreadyChecked: {
        url: false,
        message: ''
      },
      isError: false,
      isProcessing: false
    };
  },
  watch: {
    error: {
      handler() {
        this.checkError();
      },
      deep: true
    },
    params: {
      handler() {
        // 何か入力したらサーバーからのエラーメッセージを消す
        this.errorResponse = '';
      },
      deep: true
    },
    'params.url': {
      handler(value) {
        let url = value.trim();
        this.error.url = new Validation()
          .input()
          .lowerCase()
          .url()
          .domain()
          .test(url);
        // すでにアラートがない場合のみ自社ドメインかチェック
        if (!this.error.url) {
          this.checkOwnSiteDomain(url);
        }

        // 当日に調査済みのURLを入力した場合のチェック
        this.handleAlreadyCheckedUrl(url);

        this.isEdited.url = url ? true : false;
      },
      immediate: true
    },
    url: {
      handler(value) {
        if (!this.params.url) {
          this.params.url = value;
        }
      },
      immediate: true
    },
    items: {
      handler(value) {
        // 一覧を取得する前にURLが入力されていた場合に制御する
        this.handleAlreadyCheckedUrl(this.url);
      }
    }
  },
  methods: {
    checkError() {
      this.isError = utils.checkError(this.error);
    },
    focusoutUrl() {
      this.params.url = this.$options.filters.replaceDoubleSpaceToSpace(
        this.params.url
      );
    },
    handleAlreadyCheckedUrl(url) {
      // 当日調査済みのURLかどうかを判定する
      this.isAlreadyChecked.url = false;
      const today = utils.formatDate(Date.now(), 'YYYY-MM-DD');

      for (const item of this.items) {
        const date = utils.formatDate(item.surveyDate, 'YYYY-MM-DD');
        if (
          this.$options.filters.removeTrailingSlash(item.url) ===
            this.$options.filters.removeTrailingSlash(url) &&
          date === today
        ) {
          this.isAlreadyChecked.url = true;
          this.isAlreadyChecked.message =
            '本日調査済みのドメインですがよろしいですか？';
        }
      }
    },
    checkOwnSiteDomain(url) {
      // 調査ドメインと自社ドメインが同一の場合はエラーメッセージを追加
      const ownSiteDomain = this.$options.filters.getDomain(
        this.project.siteUrl
      );
      const domain = this.$options.filters.getDomain(url);

      if (ownSiteDomain === domain) {
        this.error.url =
          '自社ドメインの分析は「自社キーワード分析」をご利用ください';
      }
    },
    getUsages() {
      const params = {
        items: 'competitor-site-analysis'
      };
      return apiProjects.usages.get(params, this.auth.idToken, this.project.id);
    },
    submit() {
      if (this.isError || this.isProcessing) {
        return;
      }
      this.isProcessing = true;
      // 利用量を取得
      this.getUsages()
        .then(response => {
          let current = response.data.items[0].current;
          let max = response.data.items[0].max;
          if (current >= max) {
            this.errorResponse = `月間に登録できる上限数を超えています`;
            this.isProcessing = false;
            return;
          }

          const params = {
            url: this.params.url
          };
          apiProjects.competitorSites
            .post(params, this.auth.idToken, this.project.id)
            .then(response => {
              this.$emit('updateItems');
              this.isProcessing = false;
              this.params.url = '';
            })
            .catch(error => {
              if (error.response) {
                this.errorResponse = error.response.data.errors[0].userMessage;
              }
              this.isProcessing = false;
            });
        })
        .catch(error => {
          if (error.response) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          }
          this.isProcessing = false;
        });
    }
  }
};
</script>
